import { cn } from "@/lib/utils";
import { useState, type ReactNode } from "react";

const getHoShinSulVideoAliases = (technique: number) => [`ho_shin_sul_${technique}`];

interface LilDragonsHoShinSulTabContentProps {
  hasTrainingVideo: (aliases: string[]) => boolean;
  handleWatchTrainingVideo: (aliases: string[]) => void;
}

const HO_SHIN_SUL_TECHNIQUES: {
  titleLine: string;
  attack: string;
  steps: ReactNode;
}[] = [
  {
    titleLine: "#1 Inward Release",
    attack: "Attack: Cross grab",
    steps: (
      <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
        <li>
          Stay in natural stance. Turn your hand inwards, palm facing towards them. Don&apos;t circle
          outwards at all &ndash; instead, roll your thumb over their wrist.
        </li>
        <li>
          Don&apos;t pull your hand upwards (like looking at your watch). Take your hand across so your
          palm ends in line with your shoulder, not on your centre line.
        </li>
      </ol>
    ),
  },
  {
    titleLine: "#2 Wave Release",
    attack: "Attack: Straight grab",
    steps: (
      <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
        <li>
          I. Cat stance facing the attacking side &ndash; live hand, don&apos;t pull them in at all.
        </li>
        <li>
          II. Natural stance &ndash; roll your hand underneath and around the outside of their hand.
        </li>
        <li>III. Push slightly downwards towards their solar plexus, don&apos;t push straight.</li>
      </ol>
    ),
  },
  {
    titleLine: "#3 Circle Step Release",
    attack: "Attack: Cross grab",
    steps: (
      <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
        <li>
          Half step forward (same side as their attacking hand) &ndash; live hand, don&apos;t turn your palm
          upwards yet.
        </li>
        <li>
          Rotate your back end around 180&deg; into back stance &ndash; turn your palm upwards as you turn.
        </li>
        <li>End up facing the same way as them, next to their attacking hand.</li>
        <li>
          Lean into the front leg and keep your other arm with live fingers under their arm, gently touching.
        </li>
      </ol>
    ),
  },
  {
    titleLine: "#4 Inward Release",
    attack: "Attack: Straight grab",
    steps: (
      <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
        <li>I. Stay in natural stance. Turn your hand downwards, palm facing the floor.</li>
        <li>
          II. Step 90&deg; backwards (to the side, opposite leg to the attacking arm), sliding your hand out
          of their grip. Move your hand in a circular way following your body to end in back stance 90&deg; to
          them.
        </li>
      </ol>
    ),
  },
  {
    titleLine: "#5 Rising Release",
    attack: "Attack: Both hands grabbing one wrist",
    steps: (
      <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
        <li>Half step forward with a live hand, dipping down.</li>
        <li>
          Bring your thumb to the same shoulder; don&apos;t open your arm out, keep it tightly bent.
        </li>
        <li>Straighten up and move your elbow high, keeping your thumb against your shoulder.</li>
      </ol>
    ),
  },
];

export function LilDragonsHoShinSulTabContent({
  hasTrainingVideo,
  handleWatchTrainingVideo,
}: LilDragonsHoShinSulTabContentProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const techniqueNumber = activeIndex + 1;
  const technique = HO_SHIN_SUL_TECHNIQUES[activeIndex];
  const showVideo = hasTrainingVideo(getHoShinSulVideoAliases(techniqueNumber));

  return (
    <div>
      {/* Mobile only: title → 1–5 nav → selected technique (dragon + header column + steps) */}
      <div className="flex flex-col gap-4 md:hidden">
        <h3 className="text-lg font-semibold text-taekwondo-red">Ho Shin Sul</h3>
        <div
          className="flex w-full justify-between gap-1"
          role="tablist"
          aria-label="Ho Shin Sul techniques"
        >
          {[1, 2, 3, 4, 5].map((num, index) => {
            const isSelected = activeIndex === index;
            return (
              <button
                key={num}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-label={`Ho Shin Sul ${num}`}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "flex min-w-0 flex-1 flex-col items-center justify-center rounded-xl border-2 bg-white px-1 py-2 text-lg font-semibold tabular-nums transition-colors",
                  isSelected
                    ? "border-taekwondo-blue shadow-md shadow-taekwondo-blue/15 ring-2 ring-taekwondo-blue/25 text-taekwondo-red"
                    : "border-gray-200 text-gray-800 hover:border-taekwondo-blue/40",
                )}
              >
                {num}
              </button>
            );
          })}
        </div>
        <div
          className="bg-white rounded-lg p-4 shadow-sm flex flex-col gap-3"
          role="tabpanel"
          aria-live="polite"
        >
          <div className="flex flex-row items-start gap-3">
            <img
              src="/images/dragons/dragon_hey.png"
              alt="Lil' Dragon self-defence"
              className="h-24 w-auto flex-shrink-0"
              loading="lazy"
              decoding="async"
            />
            <div className="min-w-0 flex-1 text-left">
              <p className="text-md font-semibold text-taekwondo-red">{technique.titleLine}</p>
              <p className="mt-0.5 text-sm text-gray-700">{technique.attack}</p>
              {showVideo && (
                <button
                  type="button"
                  className="mt-2 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                  onClick={() => handleWatchTrainingVideo(getHoShinSulVideoAliases(techniqueNumber))}
                >
                  Watch Demonstration Video
                </button>
              )}
            </div>
          </div>
          <div className="text-sm text-gray-700">{technique.steps}</div>
        </div>
      </div>

      {/* Desktop: direct copy of original page markup */}
      <div className="mt-10 hidden md:block">
        <h3 className="text-lg font-semibold text-taekwondo-red mb-4">Ho Shin Sul</h3>
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_hey.png"
              alt="Lil' Dragon self-defence"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">
                #1 Inward Release &ndash; Attack: Cross grab
              </h4>
              {hasTrainingVideo(getHoShinSulVideoAliases(1)) && (
                <button
                  type="button"
                  className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                  onClick={() => handleWatchTrainingVideo(getHoShinSulVideoAliases(1))}
                >
                  Watch Demonstration Video
                </button>
              )}
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>
                  Stay in natural stance. Turn your hand inwards, palm facing towards them. Don&apos;t
                  circle outwards at all &ndash; instead, roll your thumb over their wrist.
                </li>
                <li>
                  Don&apos;t pull your hand upwards (like looking at your watch). Take your hand across
                  so your palm ends in line with your shoulder, not on your centre line.
                </li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_hey.png"
              alt="Lil' Dragon self-defence"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">
                #2 Wave Release &ndash; Attack: Straight grab
              </h4>
              {hasTrainingVideo(getHoShinSulVideoAliases(2)) && (
                <button
                  type="button"
                  className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                  onClick={() => handleWatchTrainingVideo(getHoShinSulVideoAliases(2))}
                >
                  Watch Demonstration Video
                </button>
              )}
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>
                  I. Cat stance facing the attacking side &ndash; live hand, don&apos;t pull them in at
                  all.
                </li>
                <li>
                  II. Natural stance &ndash; roll your hand underneath and around the outside of their
                  hand.
                </li>
                <li>
                  III. Push slightly downwards towards their solar plexus, don&apos;t push straight.
                </li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_hey.png"
              alt="Lil' Dragon self-defence"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">
                #3 Circle Step Release &ndash; Attack: Cross grab
              </h4>
              {hasTrainingVideo(getHoShinSulVideoAliases(3)) && (
                <button
                  type="button"
                  className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                  onClick={() => handleWatchTrainingVideo(getHoShinSulVideoAliases(3))}
                >
                  Watch Demonstration Video
                </button>
              )}
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>
                  Half step forward (same side as their attacking hand) &ndash; live hand, don&apos;t
                  turn your palm upwards yet.
                </li>
                <li>
                  Rotate your back end around 180&deg; into back stance &ndash; turn your palm upwards as
                  you turn.
                </li>
                <li>End up facing the same way as them, next to their attacking hand.</li>
                <li>
                  Lean into the front leg and keep your other arm with live fingers under their arm,
                  gently touching.
                </li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_hey.png"
              alt="Lil' Dragon self-defence"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">
                #4 Inward Release &ndash; Attack: Straight grab
              </h4>
              {hasTrainingVideo(getHoShinSulVideoAliases(4)) && (
                <button
                  type="button"
                  className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                  onClick={() => handleWatchTrainingVideo(getHoShinSulVideoAliases(4))}
                >
                  Watch Demonstration Video
                </button>
              )}
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>I. Stay in natural stance. Turn your hand downwards, palm facing the floor.</li>
                <li>
                  II. Step 90&deg; backwards (to the side, opposite leg to the attacking arm), sliding
                  your hand out of their grip. Move your hand in a circular way following your body to end
                  in back stance 90&deg; to them.
                </li>
              </ol>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_hey.png"
              alt="Lil' Dragon self-defence"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">
                #5 Rising Release &ndash; Attack: Both hands grabbing one wrist
              </h4>
              {hasTrainingVideo(getHoShinSulVideoAliases(5)) && (
                <button
                  type="button"
                  className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                  onClick={() => handleWatchTrainingVideo(getHoShinSulVideoAliases(5))}
                >
                  Watch Demonstration Video
                </button>
              )}
              <ol className="list-decimal list-inside space-y-1 marker:text-taekwondo-red">
                <li>Half step forward with a live hand, dipping down.</li>
                <li>
                  Bring your thumb to the same shoulder; don&apos;t open your arm out, keep it tightly
                  bent.
                </li>
                <li>
                  Straighten up and move your elbow high, keeping your thumb against your shoulder.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
